import { useMemo } from "react";
import { useGroupedCountsByColumnQuery } from "graphql/types/graphql";
import { Segment, SegmentationMethod } from "../types/types";
import { useHeadroomContext } from "../context/HeadRoomProvider";

export interface GroupCount {
  groupValue: string;
  count: number;
}

interface UseGroupCountsProps {
  tableName: string;
  columnName: string;
  method: SegmentationMethod;
  segments: Segment[];
  isNumeric: boolean;
}

export const useGroupCounts = ({ tableName, columnName, method, segments, isNumeric }: UseGroupCountsProps) => {
  const { shouldSkipSegmentationGroupedCountQuery } = useHeadroomContext();

  const shouldSkipQuery =
    !columnName ||
    (method === "DATA" && isNumeric) ||
    (method === "MANUAL" && (segments.length === 0 || !isNumeric || shouldSkipSegmentationGroupedCountQuery));

  const { data, loading, error } = useGroupedCountsByColumnQuery({
    variables: {
      input: {
        tableName,
        columnName,
        segments:
          method === "MANUAL" && segments.length > 0
            ? segments.map((segment, idx) => ({
                id: segment.id || idx,
                minValue: segment.minValue,
                maxValue: segment.maxValue,
              }))
            : undefined,
      },
    },
    skip: shouldSkipQuery,
  });

  const groupCounts = useMemo((): GroupCount[] => {
    if (!data?.groupedCountsByColumn?.groupCounts) return [];
    return data.groupedCountsByColumn.groupCounts.map((item) => ({
      groupValue: item.groupValue,
      count: item.count,
    }));
  }, [data]);

  return { groupCounts, loading, error };
};
